/**
 * /alerts — inbox of @-mention notifications.
 *
 * Tapping a row marks it read and navigates to the source (chat
 * message, prayer, photo, comment).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { renderWithMentions } from '../components/MentionTextarea';
import { ScreenHeader } from '../components/ScreenHeader';
import { IconButton } from '../components/IconButton';
import { IconCheck } from '../components/icons';

interface NotificationRow {
  id: string;
  actorId: string;
  actorName: string;
  sourceType: 'message' | 'prayer' | 'photo_caption' | 'photo_comment' | 'memory';
  sourceId: string;
  sourcePath: string;
  preview: string | null;
  createdAt: number;
  readAt: number | null;
}

const SOURCE_LABEL: Record<NotificationRow['sourceType'], string> = {
  message: 'in chat',
  prayer: 'in a prayer request',
  photo_caption: 'on a photo',
  photo_comment: 'in a photo comment',
  memory: 'in a memory',
};

export default function AlertsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ notifications: NotificationRow[] }>('/notifications'),
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post('/notifications/mark-all-read'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
      void qc.invalidateQueries({ queryKey: ['notifications-unread'] });
    },
  });

  const items = list.data?.notifications ?? [];
  const unread = items.filter((n) => n.readAt === null);

  function onTap(n: NotificationRow) {
    if (!n.readAt) markRead.mutate(n.id);
    nav(n.sourcePath);
  }

  return (
    <>
      <ScreenHeader
        title="Alerts"
        right={
          unread.length > 0 ? (
            <IconButton
              icon={<IconCheck />}
              label="Mark all read"
              busy={markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            />
          ) : undefined
        }
      />
      <div className="px-5 pt-4 pb-6">
        {items.length === 0 ? (
          <p className="text-muted text-center py-10 italic">
            You're all caught up. When someone @-mentions you, it shows up here.
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map((n) => (
              <li key={n.id}>
                <button
                  onClick={() => onTap(n)}
                  className={`card w-full p-4 text-left flex gap-3 ${
                    n.readAt ? 'opacity-70' : ''
                  }`}
                >
                  <span
                    aria-hidden
                    className={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${
                      n.readAt ? 'bg-transparent' : 'bg-oxblood'
                    }`}
                  />
                  <span className="flex-1">
                    <span className="block">
                      <span className="font-serif text-lg text-ink">{n.actorName}</span>
                      <span className="text-muted text-sm"> mentioned you </span>
                      <span className="text-soft text-sm">
                        {SOURCE_LABEL[n.sourceType]}
                      </span>
                    </span>
                    {n.preview && (
                      <p className="text-soft text-sm mt-1 line-clamp-2">
                        {renderWithMentions(n.preview)}
                      </p>
                    )}
                    <span className="text-xs text-muted block mt-1">
                      {new Date(n.createdAt).toLocaleString([], {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
